import Polygon from '@arcgis/core/geometry/Polygon';
import { GraphicsLayer, Point } from 'cd-eft/utils/esriTypes';
import { XyzPoint } from '../../../../models/SurveyEngineering/XyzPoint';

// The algorithm for interpolating TIN elevation is ported from the Java source code of the EFT desktop.

/**
 * get XY-plane distance from point A to point B
 * @param ptA
 * @param ptB
 * @returns
 */
export function getDistance2D(ptA: XyzPoint, ptB: XyzPoint) {
  const deltaX = ptB.getX() - ptA.getX();
  const deltaY = ptB.getY() - ptA.getY();
  return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
}

/**
 * get 3D slope distance from point A to point B
 * @param ptA
 * @param ptB
 * @returns
 */
export function getDistance3D(ptA: XyzPoint, ptB: XyzPoint) {
  const longside = getDistance2D(ptA, ptB);
  const deltaZ = ptB.getZ() - ptA.getZ();
  return Math.sqrt(longside * longside + deltaZ * deltaZ);
}

/**
 * get slope percentage from point A to point B
 * @param ptA
 * @param ptB
 * @returns
 */
export function getSlopePercentage(ptA: XyzPoint, ptB: XyzPoint) {
  const longside = getDistance2D(ptA, ptB);
  const deltaZ = ptB.getZ() - ptA.getZ();
  return deltaZ / longside * 100;
}

/**
 * interpolate an estimated Z value within a triangle defined by XyzPoint geometry.
 * Note: caller is responsible to ensure requested point is inside the triangle.
 *
 * @param p0 vertex of the triangle
 * @param p1 vertex of the triangle
 * @param p2 vertex of the triangle
 * @param x point coordinates
 * @param y point coordinates
 * @returns interpolated Z
 */
export function interpolateZ(p0: XyzPoint, p1: XyzPoint, p2: XyzPoint, x: number, y: number) {
  const EPSILON = 1.0e-4;
  // apply the interpolation formula, from Volume.interpolate,
  // adapted to use with XyzPoints
  //
  const aa = (p1.getY() - p0.getY()) * (p2.getZ() - p0.getZ()) - (p1.getZ() - p0.getZ()) * (p2.getY() - p0.getY());
  const bb = (p1.getZ() - p0.getZ()) * (p2.getX() - p0.getX()) - (p1.getX() - p0.getX()) * (p2.getZ() - p0.getZ());
  const cc = (p1.getX() - p0.getX()) * (p2.getY() - p0.getY()) - (p1.getY() - p0.getY()) * (p2.getX() - p0.getX());

  if (Math.abs(cc) > EPSILON) {
    const elevation = p0.getZ() - (aa * (x - p0.getX()) + bb * (y - p0.getY())) / cc;
    return elevation;
  } else {
    // degenerate case, cc = 0, interp. would be divide by zero

    // if requested point is in one corner, return that corner Z
    const pt = new XyzPoint(x, y, 0);
    const d0 = getDistance2D(pt, p0);
    if (d0 < EPSILON) return p0.getZ();
    const d1 = getDistance2D(pt, p1);
    if (d1 < EPSILON) return p1.getZ();
    const d2 = getDistance2D(pt, p2);
    if (d2 < EPSILON) return p2.getZ();

    // failing that, use weighted average
    // Ludicrously expensive (3 sqrt + 4 div) but should be rare...
    const prox0 = 1.0 / d0;
    const prox1 = 1.0 / d1;
    const prox2 = 1.0 / d2;
    const zAve = (prox0 * p0.getZ() + prox1 * p1.getZ() + prox2 * p2.getZ()) / (prox0 + prox1 + prox2);
    return zAve;
  }
}

/**
 * The class provides a Singleton instance for elevation interpolation.
 */
export class ElevationInterpolater {
  private static _instance: ElevationInterpolater;

  // holds 3 vertex survey points for each hit triangle to improve performance
  private triDictionary = {};

  private _surveyPointsLayer;
  private _trianglesLayer: GraphicsLayer;
  private _interpolationTriangle?: Polygon;

  /**
   * The Singleton's constructor should always be private to prevent direct
   * construction calls with the `new` operator. Please DON'T Change.
   */
  private constructor(trianglesLayer, surveyPointsLayer) {
    this._trianglesLayer = trianglesLayer;
    this._surveyPointsLayer = surveyPointsLayer;
    this.triDictionary = {}; // reset triangles cache
    // console.log('Survey points and TIN triangles layers are reloaded.');
  }

  public static Load(trianglesLayer, surveyPointsLayer) {
    ElevationInterpolater._instance = new ElevationInterpolater(trianglesLayer, surveyPointsLayer);
  }

  /**
   * The static method that controls the access to the singleton instance.
   */
  static get Current(): ElevationInterpolater {
    return ElevationInterpolater._instance;
  }

  /**
   *
   * @param x, in data source projection
   * @param y, in data source projection
   * @param mapPoint, in map view projection
   * @returns
   *
   */
  public Interpolate(x: number, y: number, mapPoint: Point) {
    let elevation;

    if (!(this._interpolationTriangle && this._interpolationTriangle.contains(mapPoint))) {
      this._interpolationTriangle = undefined;
      const containingGraphics = this._trianglesLayer.graphics.filter((item) => {
        return (item.geometry as Polygon).contains(mapPoint);
      });
      if (0 < containingGraphics.length) {
        this._interpolationTriangle = containingGraphics.getItemAt(0).geometry as Polygon;
      }
    }

    if (this._interpolationTriangle) {
      const ring = this._interpolationTriangle.rings[0];

      const p1 = new XyzPoint(ring[0][0], ring[0][1], ring[0][2]);
      const p2 = new XyzPoint(ring[1][0], ring[1][1], ring[1][2]);
      const p3 = new XyzPoint(ring[2][0], ring[2][1], ring[2][2]);

      // Doing interpolation in the map coordinates and converting the result into US feet
      elevation = (interpolateZ(p1, p2, p3, mapPoint.x, mapPoint.y) * 3937) / 1200;
    }

      // const queryParams = this._trianglesLayer.createQuery();
      // queryParams.geometry = mapPoint;
      // queryParams.spatialRelationship = 'within';
      // queryParams.returnGeometry = true;

      // const results = await this._trianglesLayer.queryFeatures(queryParams);

      // if (results.features && results.features.length > 0) {
      //   const triGeom = results.features[0].geometry;
      //   const triName = 'OID' + results.features[0].attributes.OID;
      //   if (this.triDictionary[triName]) {
      //     const [p1, p2, p3] = this.triDictionary[triName];
      //     elevation = interpolateZ(p1, p2, p3, x, y);
      //     // console.log('cached z: ' + elevation);
      //   } else {
      //     const pointQueryParams = this._surveyPointsLayer.createQuery();
      //     pointQueryParams.geometry = triGeom;
      //     pointQueryParams.spatialRelationship = 'intersects';
      //     pointQueryParams.distance = 0.1;
      //     pointQueryParams.units = 'feet';

      //     const pointsResults = await this._surveyPointsLayer.queryFeatures(pointQueryParams);
      //     if (pointsResults.features && pointsResults.features.length > 2) {
      //       const feat1 = pointsResults.features[0].attributes;
      //       const feat2 = pointsResults.features[1].attributes;

      //       const feat3 = pointsResults.features[2].attributes;

      //       const p1 = new XyzPoint(feat1.x, feat1.y, feat1.z);
      //       const p2 = new XyzPoint(feat2.x, feat2.y, feat2.z);
      //       const p3 = new XyzPoint(feat3.x, feat3.y, feat3.z);
      //       elevation = interpolateZ(p1, p2, p3, x, y);

      //       // console.log('new z: ' + elevation);
      //       this.triDictionary[triName] = [p1, p2, p3];
      //     }
      //   }
      // }

    if (elevation === undefined) {
      elevation = 0.0;
    }

    return elevation;
  }
}
